import { useEffect, useState } from "react";
import { CalendarClock, Download, ExternalLink, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Markdown } from "@/components/Markdown";
import { errMsg, useApi } from "@/lib/api";

const embedUrl = (url = "") => {
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{6,})/);
  if (yt) return `https://www.youtube-nocookie.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

export function VideoBody({ lesson }) {
  const src = embedUrl(lesson.video_url);
  return (
    <div className="space-y-6">
      {src ? (
        <div className="aspect-video overflow-hidden rounded-xl bg-black">
          <iframe src={src} title={lesson.title} loading="lazy" className="h-full w-full" allow="accelerometer; encrypted-media; picture-in-picture" allowFullScreen data-testid="lesson-video-iframe" />
        </div>
      ) : lesson.video_url && (
        <a href={lesson.video_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#222d99] underline" data-testid="lesson-video-link">Open video <ExternalLink className="h-4 w-4" /></a>
      )}
      <p className="text-xs text-slate-500">On a slow connection? Videos load only when you press play.</p>
      <Markdown text={lesson.content} />
    </div>
  );
}

export const ResourceBody = ({ lesson }) => (
  <div className="space-y-6">
    <Markdown text={lesson.content} />
    {lesson.resource_url && (
      <Button asChild variant="outline" data-testid="lesson-resource-download">
        <a href={lesson.resource_url} target="_blank" rel="noreferrer"><Download className="mr-2 h-4 w-4" /> {lesson.resource_name || "Download resource"}</a>
      </Button>
    )}
  </div>
);

export const LiveBody = ({ lesson }) => (
  <div className="space-y-6">
    <div className="flex items-center gap-3 rounded-xl border border-[#222d99]/30 bg-[#e8eaf6] p-4">
      <CalendarClock className="h-5 w-5 text-[#222d99]" />
      <p className="text-sm font-semibold text-slate-900" data-testid="lesson-live-time">{lesson.live_at || "Time to be announced"}</p>
    </div>
    <Markdown text={lesson.content} />
    {lesson.live_url && (
      <Button asChild className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="lesson-live-join">
        <a href={lesson.live_url} target="_blank" rel="noreferrer">Join live session <ExternalLink className="ml-2 h-4 w-4" /></a>
      </Button>
    )}
  </div>
);

export const ProjectBody = ({ lesson }) => <Markdown text={lesson.content} />;

export function QuizBody({ lesson, lastAttempt, canAnswer, onPassed }) {
  const api = useApi();
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(lastAttempt);
  const [busy, setBusy] = useState(false);
  const qs = lesson.quiz?.questions || [];
  const byId = Object.fromEntries((result?.results || []).map((r) => [r.id, r]));

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/quiz`, { answers });
      setResult(data);
      if (data.passed) { toast.success(`Passed with ${data.score}%`); onPassed(data.progress); }
      else toast.error(`Score ${data.score}%. You need ${lesson.quiz.pass_mark}% — try again.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="quiz-container">
      <p className="text-sm text-slate-600">Pass mark: {lesson.quiz?.pass_mark}%</p>
      {qs.map((q, i) => (
        <fieldset key={q.id} className="rounded-xl border border-slate-200 p-4" data-testid={`quiz-question-${i}`}>
          <legend className="px-1 font-semibold text-slate-900">{i + 1}. {q.question}</legend>
          <div className="mt-2 space-y-2">
            {q.options.map((o, oi) => {
              const r = byId[q.id];
              const mark = r && oi === r.answer_index ? "border-emerald-400 bg-emerald-50" : "";
              return (
                <label key={oi} className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 text-sm ${answers[q.id] === oi ? "border-[#222d99] bg-[#e8eaf6]" : "border-slate-200"} ${mark}`}>
                  <input type="radio" name={q.id} checked={answers[q.id] === oi} onChange={() => setAnswers({ ...answers, [q.id]: oi })} disabled={!canAnswer} data-testid={`quiz-q${i}-option-${oi}`} />
                  {o}
                </label>
              );
            })}
          </div>
        </fieldset>
      ))}
      {result && (
        <p className={`text-sm font-semibold ${result.passed ? "text-emerald-700" : "text-rose-600"}`} data-testid="quiz-result">
          Last score: {result.score}% — {result.passed ? "passed" : "not passed yet"}
        </p>
      )}
      {canAnswer && (
        <Button onClick={submit} disabled={busy || Object.keys(answers).length < qs.length} className="rounded-full bg-[#222d99] hover:bg-[#1a237e]" data-testid="quiz-submit-btn">
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit answers
        </Button>
      )}
    </div>
  );
}

export function DiscussionBody({ lesson, canPost }) {
  const api = useApi();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/lessons/${lesson.id}/discussion`).then((r) => setPosts(r.data)).catch(() => {});
  }, [api, lesson.id]);

  const post = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/lessons/${lesson.id}/discussion`, { text });
      setPosts((p) => [...p, data]);
      setText("");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Markdown text={lesson.content} />
      <div className="space-y-3" data-testid="discussion-posts">
        {posts.map((p) => (
          <div key={p.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold text-slate-900">{p.author} {p.is_admin && <span className="ml-1 rounded bg-[#222d99] px-1.5 py-0.5 text-[10px] text-white">Team</span>}
              <span className="ml-2 font-normal text-slate-400">{new Date(p.created_at).toLocaleString()}</span></p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{p.text}</p>
          </div>
        ))}
        {!posts.length && <p className="text-sm text-slate-500">No posts yet. Start the conversation.</p>}
      </div>
      {canPost && (
        <div className="space-y-2">
          <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder="Share your thoughts" data-testid="discussion-input" />
          <Button onClick={post} disabled={busy || text.trim().length < 2} variant="outline" data-testid="discussion-post-btn"><Send className="mr-2 h-4 w-4" /> Post</Button>
        </div>
      )}
    </div>
  );
}
